import { useState, useEffect } from 'react';
import { X, Sparkles, MapPin, Calendar, GraduationCap, Lightbulb, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentConciergeModal({ isOpen, onClose, onResultsReady }) {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [preferences, setPreferences] = useState({
    session_type: 'any',
    location_id: '',
    grade: '',
    week: '',
    stem_focus: 'any'
  });

  useEffect(() => {
    if (isOpen) {
      fetchLocations();
      loadSavedPreferences();
    }
  }, [isOpen]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/`);
      setLocations(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const loadSavedPreferences = () => {
    // Try localStorage first (for guests)
    const saved = localStorage.getItem('camp_preferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to parse saved preferences');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save preferences to localStorage
      localStorage.setItem('camp_preferences', JSON.stringify(preferences));

      // Get matches from API
      const response = await axios.post(`${API_URL}/api/parent-concierge/match/`, preferences);
      const matches = response.data.matches || [];

      if (matches.length === 0) {
        toast.error('No camps match your preferences. Try adjusting your criteria.');
        setLoading(false);
        return;
      }

      toast.success(`Found ${matches.length} perfect matches for you!`);
      
      // Pass matches to parent component
      if (onResultsReady) {
        onResultsReady(matches, preferences);
      }

      onClose();
    } catch (error) {
      console.error('Failed to get matches:', error);
      toast.error('Failed to find matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast.success('You can browse all camps manually!');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Parent Concierge</h2>
                <p className="text-blue-100 text-sm">Find your perfect summer camp in seconds</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Session Type */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              What schedule works best for you?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'any', label: 'Any Time' },
                { value: 'full_day', label: 'Full Day\n(9 AM - 4 PM)' },
                { value: 'am', label: 'Morning\n(9 AM - 12 PM)' },
                { value: 'pm', label: 'Afternoon\n(1 PM - 4 PM)' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPreferences(prev => ({ ...prev, session_type: option.value }))}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-medium whitespace-pre-line ${
                    preferences.session_type === option.value
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              Preferred location (optional)
            </label>
            <select
              value={preferences.location_id}
              onChange={(e) => setPreferences(prev => ({ ...prev, location_id: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Any location</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.name} - {loc.city}, {loc.state}
                </option>
              ))}
            </select>
          </div>

          {/* Grade */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <GraduationCap className="w-4 h-4 text-blue-600" />
              What grade is your child in?
            </label>
            <select
              value={preferences.grade}
              onChange={(e) => setPreferences(prev => ({ ...prev, grade: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select grade</option>
              <option value="K">Kindergarten</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                <option key={grade} value={grade}>Grade {grade}</option>
              ))}
            </select>
          </div>

          {/* STEM Focus */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Lightbulb className="w-4 h-4 text-blue-600" />
              What interests your child most?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { value: 'any', label: 'Surprise Me!' },
                { value: 'science', label: 'Science' },
                { value: 'technology', label: 'Technology' },
                { value: 'engineering', label: 'Engineering' },
                { value: 'math', label: 'Math' },
                { value: 'robotics', label: 'Robotics' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPreferences(prev => ({ ...prev, stem_focus: option.value }))}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    preferences.stem_focus === option.value
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Finding matches...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Find My Perfect Camp
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Skip for now
            </button>
          </div>

          {/* Info */}
          <p className="text-xs text-gray-500 text-center">
            Your preferences will be saved for faster browsing next time
          </p>
        </form>
      </div>
    </div>
  );
}
